/**
 * Navigation Board Types and Storage
 * 
 * Handles customization of Home and Entertainment screen items
 */

import { LucideIcon } from "lucide-react";
import {
  MessageSquare,
  Keyboard,
  Moon,
  Music,
  HeartHandshake,
  Youtube,
  Tv,
  Radio,
  Puzzle,
} from "lucide-react";

export interface NavigationItem {
  id: string;
  key: string;
  labelHe: string;
  labelEn: string;
  labelRu?: string;
  labelAr?: string;
  iconName: string;
  action: string;
  color: string;
  order: number;
  visible: boolean;
}

export interface NavigationBoardData {
  id: "home" | "entertainment";
  items: NavigationItem[];
  updatedAt: string;
}

// Default Home items
// Row order (RTL): Help(0)=rightmost, Rest(1), Communication(2), Keyboard(3), Entertainment(4)=leftmost
// Flower: Center=0, Right=1, Top=2, Left=3, Bottom=4
const DEFAULT_HOME_ITEMS: NavigationItem[] = [
  { id: "help", key: "help", labelHe: "עזרה", labelEn: "Help", labelRu: "Помощь", labelAr: "مساعدة", iconName: "HeartHandshake", action: "help", color: "help", order: 0, visible: true },
  { id: "rest", key: "rest", labelHe: "מנוחה", labelEn: "Rest", labelRu: "Отдых", labelAr: "راحة", iconName: "Moon", action: "/rest", color: "teal", order: 1, visible: true },
  { id: "communication", key: "communication", labelHe: "לוח תקשורת", labelEn: "Talker", labelRu: "Говорить", labelAr: "لوحة التواصل", iconName: "MessageSquare", action: "/communication", color: "green", order: 2, visible: true },
  { id: "keyboard", key: "keyboard", labelHe: "מקלדת", labelEn: "Keyboard", labelRu: "Быстрый набор", labelAr: "لوحة مفاتيح", iconName: "Keyboard", action: "/aac-keyboard", color: "pink", order: 3, visible: true },
  { id: "entertainment", key: "entertainment", labelHe: "בידור", labelEn: "Media", labelRu: "Медиа", labelAr: "وسائط", iconName: "Music", action: "/entertainment", color: "purple", order: 4, visible: true },
];

// Default Entertainment items
// Row order: YouTube(0), Radio(1)
const DEFAULT_ENTERTAINMENT_ITEMS: NavigationItem[] = [
  { id: "youtube", key: "youtube", labelHe: "יוטיוב", labelEn: "YouTube", labelRu: "YouTube", labelAr: "يوتيوب", iconName: "Youtube", action: "/youtube", color: "help", order: 0, visible: true },
  { id: "radio", key: "radio", labelHe: "רדיו", labelEn: "Radio", labelRu: "Радио", labelAr: "راديو", iconName: "Radio", action: "/radio", color: "purple", order: 1, visible: true },
];

const STORAGE_KEY = "navigationBoards_v17"; // v17: RU communication label → "Говорить"
const OLD_STORAGE_KEY = "navigationBoards_v16";

// Valid color categories
const VALID_COLORS = ["communication", "media", "utility", "help", "orange", "blue", "green", "purple", "pink", "yellow", "indigo", "teal"];

// Migrate items: enrich with missing labelRu/labelAr from defaults
function migrateNavItems(items: NavigationItem[], defaults: NavigationItem[]): NavigationItem[] {
  return items.map(item => {
    const def = defaults.find(d => d.key === item.key);
    if (def) {
      return {
        ...item,
        labelRu: item.labelRu || def.labelRu,
        labelAr: item.labelAr || def.labelAr,
      };
    }
    return item;
  });
}

// Valid action paths for entertainment items
const VALID_ENTERTAINMENT_ACTIONS: Record<string, string> = {
  youtube: "/youtube",
  radio: "/radio",
};

// Load board from localStorage
export function loadNavigationBoard(boardId: "home" | "entertainment"): NavigationBoardData {
  const defaults = boardId === "home" ? DEFAULT_HOME_ITEMS : DEFAULT_ENTERTAINMENT_ITEMS;
  
  // Try current version
  let raw = localStorage.getItem(STORAGE_KEY);
  
  // Migrate from old version if needed
  if (!raw && OLD_STORAGE_KEY) {
    const oldRaw = localStorage.getItem(OLD_STORAGE_KEY);
    if (oldRaw) {
      // Migrate: copy to new key, remove old
      localStorage.setItem(STORAGE_KEY, oldRaw);
      localStorage.removeItem(OLD_STORAGE_KEY);
      raw = oldRaw;
    }
  }
  
  try {
    if (raw) {
      const boards: NavigationBoardData[] = JSON.parse(raw);
      const board = boards.find(b => b.id === boardId);
      if (board) {
        // Enrich with missing labelRu/labelAr and fix colors/actions
        board.items = migrateNavItems(board.items, defaults).map(item => {
          let fixedItem = { ...item };
          
          // Also update labelRu from defaults if it matches old value (force update)
          const def = defaults.find(d => d.key === item.key);
          if (def && def.labelRu && (!item.labelRu || item.labelRu !== def.labelRu)) {
            fixedItem.labelRu = def.labelRu;
          }
          
          // Fix invalid colors
          if (!VALID_COLORS.includes(item.color)) {
            fixedItem.color = def?.color || "utility";
          }
          
          // Fix invalid actions for entertainment items
          if (boardId === "entertainment" && VALID_ENTERTAINMENT_ACTIONS[item.key]) {
            fixedItem.action = VALID_ENTERTAINMENT_ACTIONS[item.key];
          }
          
          return fixedItem;
        });
        
        // Persist migrated data
        const allBoards: NavigationBoardData[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const idx = allBoards.findIndex(b => b.id === boardId);
        if (idx >= 0) allBoards[idx] = board;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(allBoards));
        
        return board;
      }
    }
  } catch (e) {
    console.error("Failed to load navigation board:", e);
  }
  
  // Return default
  return {
    id: boardId,
    items: [...defaults],
    updatedAt: new Date().toISOString(),
  };
}

// Save board to localStorage
export function saveNavigationBoard(board: NavigationBoardData): void {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    let boards: NavigationBoardData[] = saved ? JSON.parse(saved) : [];
    
    const index = boards.findIndex(b => b.id === board.id);
    if (index >= 0) {
      boards[index] = board;
    } else {
      boards.push(board);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(boards));
    // Notify listeners (e.g., Home/Entertainment screens) that board data changed
    window.dispatchEvent(new CustomEvent('navigationBoardUpdated', { detail: { boardId: board.id } }));
  } catch (e) {
    console.error("Failed to save navigation board:", e);
  }
}

// Update a single item
export function updateNavigationItem(
  board: NavigationBoardData,
  itemId: string,
  updates: Partial<Omit<NavigationItem, 'id' | 'key' | 'action'>>
): NavigationBoardData {
  return {
    ...board,
    items: board.items.map(item =>
      item.id === itemId ? { ...item, ...updates } : { ...item }
    ),
    updatedAt: new Date().toISOString(),
  };
}

// Reorder items
export function reorderNavigationItems(
  board: NavigationBoardData,
  fromIndex: number,
  toIndex: number
): NavigationBoardData {
  // Important: the editor reorders based on the *display order* (order field),
  // not necessarily the underlying array order. Normalize first.
  const orderedItems = [...board.items]
    .sort((a, b) => a.order - b.order)
    .map(item => ({ ...item }));

  const [moved] = orderedItems.splice(fromIndex, 1);
  orderedItems.splice(toIndex, 0, moved);

  // Update order property on new objects
  const updatedItems = orderedItems.map((item, idx) => ({
    ...item,
    order: idx,
  }));
  
  return {
    ...board,
    items: updatedItems,
    updatedAt: new Date().toISOString(),
  };
}

// Reset to defaults
export function resetNavigationBoard(boardId: "home" | "entertainment"): NavigationBoardData {
  const board: NavigationBoardData = {
    id: boardId,
    items: boardId === "home" ? [...DEFAULT_HOME_ITEMS] : [...DEFAULT_ENTERTAINMENT_ITEMS],
    updatedAt: new Date().toISOString(),
  };
  saveNavigationBoard(board);
  return board;
}

// Get default items for reference
export function getDefaultItems(boardId: "home" | "entertainment"): NavigationItem[] {
  return boardId === "home" ? DEFAULT_HOME_ITEMS : DEFAULT_ENTERTAINMENT_ITEMS;
}
